import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { ChartDigits } from '@efacture/accounting';
import type { DecimalString } from '@efacture/web-billing/lib/decimal';

// ─────────────────────────────────────────────────────────────
// Types (miroir des modèles Prisma — les Decimal arrivent en string)
// ─────────────────────────────────────────────────────────────

export type EntryStatus = 'DRAFT' | 'POSTED' | 'REVERSED';
export type PeriodStatus = 'OPEN' | 'LOCKED' | 'CLOSED';
export type ExerciseStatus = 'OPEN' | 'CLOSING' | 'CLOSED';
export type NormalBalance = 'DEBIT' | 'CREDIT';

/**
 * Journal comptable — système (VT/AC/BQ/CA/OD/AN, seedés à l'activation) ou
 * personnalisé (journal de trésorerie par banque créé par le tenant, retour
 * UAT comptable §3 #5). `accountNumber` est le compte de trésorerie associé
 * (contrepartie suggérée à la saisie) — `null` pour les journaux système
 * (leur contrepartie passe par le mapping rôle→compte existant) et pour tout
 * journal non-TRESORERIE. Source de vérité: GET /api/accounting/journals —
 * plus de liste statique côté web (un tenant peut créer BQ1, BQ2…).
 */
export interface Journal {
  id: string;
  code: string;
  label: string;
  isSystem: boolean;
  type: string | null;
  accountNumber: string | null;
  /**
   * Journal en service (D-5). Un journal retiré n'est plus proposé à la
   * SAISIE, mais reste listé ici : les filtres et les rapports en
   * dépendent pour afficher son historique — le masquer rouvrirait le
   * trou d'audit qui interdit la suppression.
   */
  isActive: boolean;
}

export interface JournalListResponse {
  journals: Journal[];
}

export interface CreateJournalData {
  code: string;
  label: string;
  type: 'TRESORERIE';
  accountNumber: string;
}

export interface UpdateJournalData {
  label?: string;
  accountNumber?: string;
  /** D-5 — retrait / remise en service (journaux du tenant uniquement). */
  isActive?: boolean;
}

/** Libellé d'affichage uniforme d'un journal dans les sélecteurs. */
export function journalOptionLabel(journal: Journal): string {
  return `${journal.code} — ${journal.label}`;
}

export interface Account {
  id: string;
  number: string;
  label: string;
  class: number;
  parentNumber: string | null;
  normalBalance: NormalBalance;
  isPostable: boolean;
  isLetterable: boolean;
  isReconcilable: boolean;
  isSystem: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface JournalLine {
  id: string;
  entryId: string;
  lineNumber: number;
  accountId: string;
  accountNumber: string;
  accountLabel: string;
  date: string;
  journalCode: string;
  debit: DecimalString;
  credit: DecimalString;
  label: string | null;
  auxiliaryType: string | null;
  auxiliaryId: string | null;
  /** Compte tiers (comptes tiers, spec 2026-07-24 §4) — AuxiliaryAccount.id. */
  auxiliaryAccountId?: string | null;
  letteringId: string | null;
}

export interface JournalEntry {
  id: string;
  journalCode: string;
  exerciseYear: number;
  number: number | null;
  entryNumber: string | null;
  date: string;
  periodId: string;
  label: string;
  reference: string | null;
  status: EntryStatus;
  sourceType: string;
  sourceId: string | null;
  totalDebit: DecimalString;
  totalCredit: DecimalString;
  reversalOfId: string | null;
  postedAt: string | null;
  createdAt: string;
  updatedAt: string;
  lines: JournalLine[];
}

export interface AccountingPeriod {
  id: string;
  exerciseId: string;
  month: number;
  startDate: string;
  endDate: string;
  status: PeriodStatus;
  lockedAt: string | null;
  lockedById: string | null;
}

export interface AccountingExercise {
  id: string;
  year: number;
  startDate: string;
  endDate: string;
  status: ExerciseStatus;
  periods: AccountingPeriod[];
}

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasMore: boolean;
}

// ─────────────────────────────────────────────────────────────
// Paramètres et corps de requêtes
// ─────────────────────────────────────────────────────────────

export interface AccountListParams {
  class?: number | undefined;
  search?: string | undefined;
  postableOnly?: boolean | undefined;
}

export interface CreateAccountData {
  number: string;
  label: string;
  normalBalance?: NormalBalance;
  isPostable?: boolean;
  isLetterable?: boolean;
  isReconcilable?: boolean;
}

export interface UpdateAccountData {
  label?: string;
  isActive?: boolean;
  isLetterable?: boolean;
  isReconcilable?: boolean;
}

export interface EntryLineInput {
  accountId: string;
  debit: number;
  credit: number;
  label?: string;
  /**
   * Rattachement auxiliaire (round-trip obligatoire à la modification) —
   * CLIENT (411x) | SUPPLIER (401x) | EMPLOYEE (422x, personnel — retour
   * UAT comptable §3 #6).
   */
  auxiliaryType?: 'CLIENT' | 'SUPPLIER' | 'EMPLOYEE';
  auxiliaryId?: string;
  /**
   * Compte tiers (spec 2026-07-24 §4) — remplace la paire legacy dans la
   * saisie (sélecteur typeahead) ; le serveur résout l'un depuis l'autre
   * (résolution croisée), l'API legacy reste acceptée.
   */
  auxiliaryAccountId?: string;
}

export interface EntryDraftInput {
  journalCode: string;
  date: string; // YYYY-MM-DD
  label: string;
  reference?: string;
  lines: EntryLineInput[];
}

export interface EntryListParams {
  journalCode?: string | undefined;
  status?: EntryStatus | undefined;
  from?: string | undefined; // YYYY-MM-DD
  to?: string | undefined; // YYYY-MM-DD
  /** Recherche serveur, insensible à la casse : numéro, libellé, référence. */
  search?: string | undefined;
  page?: number | undefined;
  limit?: number | undefined;
}

export interface ReverseEntryData {
  date: string; // YYYY-MM-DD
  label?: string;
}

// ─────────────────────────────────────────────────────────────
// Réponses (tableaux nommés contextuellement — pitfall CLAUDE.md)
// ─────────────────────────────────────────────────────────────

export interface AccountListResponse {
  accounts: Account[];
}

export interface EntryListResponse {
  entries: JournalEntry[];
  pagination: PaginationInfo;
  /** Per-employee pay lines aggregated by the server (no payroll:read). */
  employeeDetailWithheld?: boolean;
}

/** GET /entries/:id: the entry and whether its pay lines were aggregated. */
export type JournalEntryDetail = JournalEntry & { employeeDetailWithheld: boolean };

export interface PeriodsResponse {
  year: number;
  exercise: AccountingExercise | null;
}

export interface ActivationResult {
  accountingEnabled: boolean;
  accountsCreated: number;
  journalsCreated: number;
  mappingsCreated: number;
}

/** What POST /accounting/chart/update added to a seeded chart. */
export interface ChartUpdateResult {
  accountsCreated: number;
  journalsCreated: number;
  mappingsCreated: number;
}

/**
 * Préférence d'affichage/saisie du plan comptable (retour UAT
 * comptable §3 #7) — n'affecte jamais le numéro de compte STOCKÉ
 * (voir @efacture/accounting: displayAccountNumber / resolveAccountInput).
 */
export interface ChartDigitsSettings {
  chartDigits: ChartDigits;
}

/**
 * Agrément d'amortissement accéléré (CGI Art. 77.3) + régime fiscal du
 * dossier — les DEUX faits d'entreprise qu'`assertFiscalMethodAllowed`
 * (@efacture/accounting) consulte pour autoriser un plan fiscal.
 *
 * L'agrément n'est JAMAIS validé : le CGI n'en décrit ni la forme, ni
 * l'organe, ni le délai, ni le contenu — seule sa PRÉSENCE déclarative
 * l'est. `taxRegime` est en lecture seule ici (il se modifie dans les
 * paramètres de l'entreprise) ; l'Art. 78.1 réserve le dégressif comme
 * l'accéléré au bénéfice réel (RNI, RSI), donc MICRO est exclu.
 */
export interface AcceleratedDepreciationSettings {
  acceleratedDepreciationApproved: boolean;
  acceleratedApprovalRef: string | null;
  acceleratedApprovalDate: string | null;
  taxRegime: string | null;
}

/** Corps du PATCH — activer EXIGE une référence non vide (Art. 77.3). */
export interface AcceleratedDepreciationInput {
  acceleratedDepreciationApproved: boolean;
  acceleratedApprovalRef?: string;
  acceleratedApprovalDate?: string;
}

export interface ReverseEntryResponse {
  original: JournalEntry;
  reversal: JournalEntry;
}

// ─────────────────────────────────────────────────────────────
// Aide Decimal — les colonnes Decimal Prisma sont sérialisées en string
// ─────────────────────────────────────────────────────────────

export { toNum } from '@efacture/web-billing/lib/decimal';

// ─────────────────────────────────────────────────────────────
// Query Keys
// ─────────────────────────────────────────────────────────────

export const accountingKeys = {
  all: ['accounting'] as const,
  accounts: () => [...accountingKeys.all, 'accounts'] as const,
  accountList: (params: AccountListParams) => [...accountingKeys.accounts(), params] as const,
  entries: () => [...accountingKeys.all, 'entries'] as const,
  entryList: (params: EntryListParams) => [...accountingKeys.entries(), 'list', params] as const,
  entryDetail: (id: string) => [...accountingKeys.entries(), 'detail', id] as const,
  periods: () => [...accountingKeys.all, 'periods'] as const,
  periodsByYear: (year: number) => [...accountingKeys.periods(), year] as const,
  journals: () => [...accountingKeys.all, 'journals'] as const,
  chartDigits: () => [...accountingKeys.all, 'chart-digits'] as const,
  acceleratedDepreciation: () =>
    [...accountingKeys.all, 'accelerated-depreciation'] as const,
};

// ─────────────────────────────────────────────────────────────
// Fonctions API
// ─────────────────────────────────────────────────────────────

async function fetchAccounts(params: AccountListParams): Promise<AccountListResponse> {
  return apiClient.get<AccountListResponse>('/accounting/accounts', {
    class: params.class,
    search: params.search,
    postableOnly: params.postableOnly || undefined,
  });
}

async function createAccount(data: CreateAccountData): Promise<Account> {
  const result = await apiClient.post<{ account: Account }>('/accounting/accounts', data);
  return result.account;
}

async function updateAccount(id: string, data: UpdateAccountData): Promise<Account> {
  const result = await apiClient.patch<{ account: Account }>(`/accounting/accounts/${id}`, data);
  return result.account;
}

async function deleteAccount(id: string): Promise<void> {
  await apiClient.delete(`/accounting/accounts/${id}`);
}

async function activateAccounting(): Promise<ActivationResult> {
  const result = await apiClient.post<{ activation: ActivationResult }>('/accounting/activate');
  return result.activation;
}

async function updateChartOfAccounts(): Promise<ChartUpdateResult> {
  const result = await apiClient.post<{ update: ChartUpdateResult }>('/accounting/chart/update');
  return result.update;
}

async function fetchChartDigits(): Promise<ChartDigitsSettings> {
  return apiClient.get<ChartDigitsSettings>('/accounting/settings/chart-digits');
}

async function setChartDigits(chartDigits: ChartDigits): Promise<ChartDigitsSettings> {
  return apiClient.patch<ChartDigitsSettings>('/accounting/settings/chart-digits', {
    chartDigits,
  });
}

async function fetchAcceleratedDepreciation(): Promise<AcceleratedDepreciationSettings> {
  return apiClient.get<AcceleratedDepreciationSettings>(
    '/accounting/settings/accelerated-depreciation'
  );
}

async function setAcceleratedDepreciation(
  input: AcceleratedDepreciationInput
): Promise<AcceleratedDepreciationSettings> {
  return apiClient.patch<AcceleratedDepreciationSettings>(
    '/accounting/settings/accelerated-depreciation',
    input
  );
}

async function fetchEntries(params: EntryListParams): Promise<EntryListResponse> {
  return apiClient.get<EntryListResponse>('/accounting/entries', {
    journalCode: params.journalCode,
    status: params.status,
    from: params.from,
    to: params.to,
    search: params.search,
    page: params.page,
    limit: params.limit,
  });
}

async function fetchEntry(id: string): Promise<JournalEntryDetail> {
  const result = await apiClient.get<{ entry: JournalEntry; employeeDetailWithheld?: boolean }>(
    `/accounting/entries/${id}`
  );
  return { ...result.entry, employeeDetailWithheld: result.employeeDetailWithheld === true };
}

async function createEntry(data: EntryDraftInput): Promise<JournalEntry> {
  const result = await apiClient.post<{ entry: JournalEntry }>('/accounting/entries', data);
  return result.entry;
}

async function updateEntry(id: string, data: EntryDraftInput): Promise<JournalEntry> {
  const result = await apiClient.put<{ entry: JournalEntry }>(`/accounting/entries/${id}`, data);
  return result.entry;
}

async function deleteEntry(id: string): Promise<void> {
  await apiClient.delete(`/accounting/entries/${id}`);
}

async function postEntry(id: string): Promise<JournalEntry> {
  const result = await apiClient.post<{ entry: JournalEntry }>(`/accounting/entries/${id}/post`);
  return result.entry;
}

async function reverseEntry(id: string, data: ReverseEntryData): Promise<ReverseEntryResponse> {
  return apiClient.post<ReverseEntryResponse>(`/accounting/entries/${id}/reverse`, data);
}

async function fetchJournals(): Promise<JournalListResponse> {
  return apiClient.get<JournalListResponse>('/accounting/journals');
}

async function createJournal(data: CreateJournalData): Promise<Journal> {
  const result = await apiClient.post<{ journal: Journal }>('/accounting/journals', data);
  return result.journal;
}

async function updateJournal(id: string, data: UpdateJournalData): Promise<Journal> {
  const result = await apiClient.put<{ journal: Journal }>(`/accounting/journals/${id}`, data);
  return result.journal;
}

async function fetchPeriods(year?: number): Promise<PeriodsResponse> {
  return apiClient.get<PeriodsResponse>('/accounting/periods', { year });
}

async function lockPeriod(id: string): Promise<AccountingPeriod> {
  const result = await apiClient.post<{ period: AccountingPeriod }>(
    `/accounting/periods/${id}/lock`
  );
  return result.period;
}

async function unlockPeriod(id: string): Promise<AccountingPeriod> {
  const result = await apiClient.post<{ period: AccountingPeriod; auditNote: string }>(
    `/accounting/periods/${id}/unlock`
  );
  return result.period;
}

// ─────────────────────────────────────────────────────────────
// Queries — Plan comptable
// ─────────────────────────────────────────────────────────────

/** Liste du plan comptable. GET /api/accounting/accounts */
export function useAccounts(params: AccountListParams = {}, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: accountingKeys.accountList(params),
    queryFn: () => fetchAccounts(params),
    enabled: options?.enabled ?? true,
  });
}

/** Création d'un sous-compte. POST /api/accounting/accounts */
export function useCreateAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAccountData) => createAccount(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountingKeys.accounts() });
    },
  });
}

/** Mise à jour d'un compte (libellé, activation, flags). PATCH /api/accounting/accounts/:id */
export function useUpdateAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAccountData }) => updateAccount(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountingKeys.accounts() });
    },
  });
}

/** Suppression d'un compte jamais mouvementé. DELETE /api/accounting/accounts/:id */
export function useDeleteAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteAccount(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountingKeys.accounts() });
    },
  });
}

/**
 * Préférence 4/5/6 chiffres du plan comptable (retour UAT comptable
 * §3 #7) — GET /api/accounting/settings/chart-digits (accounting:read)
 */
export function useChartDigitsSettings(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: accountingKeys.chartDigits(),
    queryFn: fetchChartDigits,
    staleTime: 1000 * 60,
    enabled: options?.enabled ?? true,
  });
}

/** Modifie la préférence. PATCH /api/accounting/settings/chart-digits (accounting:settings) */
export function useSetChartDigits() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (chartDigits: ChartDigits) => setChartDigits(chartDigits),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountingKeys.chartDigits() });
    },
  });
}

/**
 * Agrément Art. 77.3 + régime fiscal du dossier.
 * GET /api/accounting/settings/accelerated-depreciation (accounting:read)
 *
 * Lu par les paramètres comptables (pour le déclarer) ET par le
 * formulaire d'immobilisation (pour refuser l'accéléré en clair avant
 * l'envoi, plutôt que de laisser le serveur le faire).
 */
export function useAcceleratedDepreciationSettings(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: accountingKeys.acceleratedDepreciation(),
    queryFn: fetchAcceleratedDepreciation,
    staleTime: 1000 * 60,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Déclare ou révoque l'agrément.
 * PATCH /api/accounting/settings/accelerated-depreciation (accounting:settings)
 */
export function useSetAcceleratedDepreciation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AcceleratedDepreciationInput) => setAcceleratedDepreciation(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: accountingKeys.acceleratedDepreciation(),
      });
    },
  });
}

/** Activation de la comptabilité (semence du plan). POST /api/accounting/activate */
export function useActivateAccounting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => activateAccounting(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountingKeys.all });
    },
  });
}

/** Adds the missing reference accounts to a seeded chart; never switches accounting on. */
export function useUpdateChartOfAccounts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => updateChartOfAccounts(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountingKeys.all });
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Queries — Écritures
// ─────────────────────────────────────────────────────────────

/** Liste paginée des écritures. GET /api/accounting/entries */
/**
 * ★ TOUTES les écritures correspondant au filtre, page après page —
 * pour l'EXPORT (constat C1).
 *
 * L'export CSV/XLSX écrivait la page affichée (25 lignes) sous une
 * légende qui annonce le total du filtre : « 142 écriture(s) — page 1
 * sur 6 » puis un fichier de 25 lignes remis au comptable, sans que rien
 * à l'écran ni dans le fichier ne signale la troncature. Une perte de
 * données silencieuse dans un livrable.
 *
 * La route plafonne `limit` à 100 : on pagine donc, et on s'arrête sur le
 * `total` annoncé par le serveur. Le garde-fou à 200 pages (20 000
 * écritures) empêche une boucle infinie si la pagination dérive ; il est
 * volontairement très au-dessus de tout exercice réel.
 */
export async function fetchAllEntriesForExport(
  params: EntryListParams
): Promise<JournalEntry[]> {
  const PAGE_SIZE = 100;
  const MAX_PAGES = 200;
  const all: JournalEntry[] = [];
  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const res = await fetchEntries({ ...params, page, limit: PAGE_SIZE });
    all.push(...res.entries);
    // Page vide : divergence de pagination cote serveur — on rend ce qui
    // existe. `return`, jamais `break` : sortir de la boucle tomberait sur
    // le `throw` du garde-fou et transformerait une fin normale en echec.
    if (res.entries.length === 0) return all;
    if (all.length >= res.pagination.total) return all;
  }
  // ★ Atteindre le garde-fou est une ERREUR, pas une fin de boucle (revue
  // croisee du 2026-08-10). Rendre les 20 000 premieres lignes
  // reproduirait EXACTEMENT le defaut C1 — un fichier court sous une
  // legende qui promet le total — simplement a un seuil plus haut. C'est
  // le silence qui est le defaut, pas le nombre.
  throw new Error(
    `Export trop volumineux : ce filtre dépasse ${MAX_PAGES * PAGE_SIZE} écritures. ` +
      "Restreignez la période ou le journal, puis relancez l'export — " +
      'aucun fichier tronqué ne sera produit.'
  );
}

export function useJournalEntries(params: EntryListParams = {}, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: accountingKeys.entryList(params),
    queryFn: () => fetchEntries(params),
    enabled: options?.enabled ?? true,
  });
}

/** Détail d'une écriture avec lignes. GET /api/accounting/entries/:id */
export function useJournalEntry(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: accountingKeys.entryDetail(id),
    queryFn: () => fetchEntry(id),
    enabled: (options?.enabled ?? true) && !!id,
  });
}

/** Création d'un brouillon d'écriture. POST /api/accounting/entries */
export function useCreateJournalEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: EntryDraftInput) => createEntry(data),
    onSuccess: (entry) => {
      queryClient.setQueryData(accountingKeys.entryDetail(entry.id), entry);
      queryClient.invalidateQueries({ queryKey: accountingKeys.entries() });
    },
  });
}

/** Modification d'un brouillon. PUT /api/accounting/entries/:id */
export function useUpdateJournalEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: EntryDraftInput }) => updateEntry(id, data),
    onSuccess: (entry) => {
      queryClient.setQueryData(accountingKeys.entryDetail(entry.id), entry);
      queryClient.invalidateQueries({ queryKey: accountingKeys.entries() });
    },
  });
}

/** Suppression d'un brouillon. DELETE /api/accounting/entries/:id */
export function useDeleteJournalEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteEntry(id),
    onSuccess: (_, deletedId) => {
      queryClient.removeQueries({ queryKey: accountingKeys.entryDetail(deletedId) });
      queryClient.invalidateQueries({ queryKey: accountingKeys.entries() });
    },
  });
}

/** Validation d'une écriture (immuable ensuite). POST /api/accounting/entries/:id/post */
export function usePostJournalEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => postEntry(id),
    onSuccess: (entry) => {
      queryClient.setQueryData(accountingKeys.entryDetail(entry.id), entry);
      queryClient.invalidateQueries({ queryKey: accountingKeys.entries() });
      // La validation peut créer l'exercice / toucher les périodes
      queryClient.invalidateQueries({ queryKey: accountingKeys.periods() });
    },
  });
}

/** Contre-passation d'une écriture validée. POST /api/accounting/entries/:id/reverse */
export function useReverseJournalEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ReverseEntryData }) => reverseEntry(id, data),
    onSuccess: (result) => {
      queryClient.setQueryData(accountingKeys.entryDetail(result.reversal.id), result.reversal);
      queryClient.invalidateQueries({ queryKey: accountingKeys.entries() });
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Queries — Périodes
// ─────────────────────────────────────────────────────────────

/** Exercice + 12 périodes d'une année. GET /api/accounting/periods?year= */
export function useAccountingPeriods(year: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: accountingKeys.periodsByYear(year),
    queryFn: () => fetchPeriods(year),
    enabled: options?.enabled ?? true,
  });
}

/** Verrouillage d'une période. POST /api/accounting/periods/:id/lock */
export function useLockPeriod() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => lockPeriod(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountingKeys.periods() });
    },
  });
}

/** Déverrouillage d'une période. POST /api/accounting/periods/:id/unlock */
export function useUnlockPeriod() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unlockPeriod(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountingKeys.periods() });
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Queries — Journaux (système + personnalisés, trésorerie par banque)
// ─────────────────────────────────────────────────────────────

/** Liste des journaux du tenant. GET /api/accounting/journals */
export function useJournals(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: accountingKeys.journals(),
    queryFn: () => fetchJournals(),
    enabled: options?.enabled ?? true,
  });
}

/** Création d'un journal de trésorerie personnalisé. POST /api/accounting/journals */
export function useCreateJournal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateJournalData) => createJournal(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountingKeys.journals() });
    },
  });
}

/** Modification d'un journal personnalisé (libellé, compte). PUT /api/accounting/journals/:id */
export function useUpdateJournal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateJournalData }) => updateJournal(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountingKeys.journals() });
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Exports utilitaires
// ─────────────────────────────────────────────────────────────

export {
  fetchAccounts,
  createAccount,
  updateAccount,
  deleteAccount,
  activateAccounting,
  fetchEntries,
  fetchEntry,
  createEntry,
  updateEntry,
  deleteEntry,
  postEntry,
  reverseEntry,
  fetchPeriods,
  lockPeriod,
  unlockPeriod,
  fetchJournals,
  createJournal,
  updateJournal,
};
